import type { DeepReadonly } from 'vue'
import type {
  IAiChatOrganization,
  IAiChatCredit,
  IAiChatDeposit,
  IAiChatDepositCalculation,
  IAiChatCreditCalculation,
  IAiChatCard,
  IAiChatExchangeRate,
  IAiChatExchangeRateProvider,
  IAiChatInsuranceProduct,
  IAiChatComparisonItem,
  IAiChatAbout
} from '~/components/entity/ai-chat'

export type { IAiChatOrganization, IAiChatComparisonItem }

export interface AiChatResults {
  organizations?: IAiChatOrganization[]
  credits?: IAiChatCredit[]
  deposits?: IAiChatDeposit[]
  deposit_calculations?: IAiChatDepositCalculation[]
  credit_calculations?: IAiChatCreditCalculation[]
  cards?: IAiChatCard[]
  exchange_rates?: IAiChatExchangeRate[]
  exchange_rate_providers?: IAiChatExchangeRateProvider[]
  insurance_products?: IAiChatInsuranceProduct[]
  comparison?: IAiChatComparisonItem[]
  about?: IAiChatAbout[]
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  // Raw reply text. Assistant replies may contain [[key]] placeholders marking
  // where each results group should be rendered (key === a key of AiChatResults).
  text: string
  results?: AiChatResults
}

export const renderMarkdown = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1</a>')
    .replace(/^(\d+)\. /gm, '<br><span class="font-medium">$1.</span> ')
    .replace(/^- /gm, '<br>• ')
    .replace(/\n/g, '<br>')
    .replace(/^<br>/, '')
}

export type AiChatResultKey = keyof AiChatResults

// One group segment per result key, each carrying its correctly-typed items.
type GroupSegment = {
  [K in AiChatResultKey]: { kind: 'group'; key: K; items: NonNullable<AiChatResults[K]> }
}[AiChatResultKey]

export type MessageSegment = { kind: 'text'; html: string } | GroupSegment

const PLACEHOLDER_RE = /\[\[(\w+)\]\]/g

// The backend may send multiple groups of the same type in one reply as
// `credits_1`, `credits_2`, `comparison_2`, etc. Each resolves to its base
// segment type; the numeric suffix only disambiguates the placeholder/results
// pairing, so segments render as independent groups.
const NUMERIC_SUFFIX_RE = /_\d+$/

const resolveResultKey = (rawKey: string): AiChatResultKey =>
  rawKey.replace(NUMERIC_SUFFIX_RE, '') as AiChatResultKey

// Splits an assistant reply into ordered segments: rendered markdown text and
// result groups, placing each group where its [[key]] placeholder appears.
export const buildSegments = (
  text: string,
  results?: DeepReadonly<AiChatResults>
): MessageSegment[] => {
  const segments: MessageSegment[] = []
  let lastIndex = 0

  const pushText = (raw: string) => {
    const trimmed = raw.trim()
    if (trimmed) segments.push({ kind: 'text', html: renderMarkdown(trimmed) })
  }

  const resultsByRawKey = results as Record<string, DeepReadonly<AiChatResults[AiChatResultKey]>> | undefined

  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    pushText(text.slice(lastIndex, match.index))
    lastIndex = match.index + match[0].length

    const rawKey = match[1]
    const key = resolveResultKey(rawKey)
    const items = resultsByRawKey?.[rawKey]
    if (items?.length) segments.push({ kind: 'group', key, items } as GroupSegment)
  }
  pushText(text.slice(lastIndex))

  return segments
}

interface AssistantResponse {
  status: string
  message: string
  data: {
    conversation_id: string
    reply: string
    client_token?: string
    results?: AiChatResults
  }
}

export const useAiChat = () => {
  const { t } = useI18n()
  const { $api } = useNuxtApp()
  const { trackEvent } = useAnalytics()

  const messages = ref<ChatMessage[]>([])
  const conversationId = ref<string | null>(null)
  const clientToken = ref<string | null>(null)
  const isLoading = ref(false)

  const sendMessage = async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || isLoading.value) return

    messages.value.push({ role: 'user', text: trimmed })
    isLoading.value = true

    try {
      const response = await $api<AssistantResponse>('/assistant/chat', {
        method: 'POST',
        body: {
          message: trimmed,
          conversation_id: conversationId.value,
          client_token: clientToken.value
        }
      })

      conversationId.value = response.data.conversation_id
      clientToken.value = response.data.client_token ?? clientToken.value
      messages.value.push({
        role: 'assistant',
        text: response.data.reply,
        results: response.data.results
      })
      trackEvent('ai_chat_message', {})
    } catch (error) {
      console.error('[useAiChat] sendMessage error:', error)
      messages.value.push({ role: 'assistant', text: t('widget_ai_chat.error') })
    } finally {
      isLoading.value = false
    }
  }

  return {
    messages: readonly(messages),
    isLoading: readonly(isLoading),
    sendMessage
  }
}
